import FullscreenOutlined from "@ant-design/icons/FullscreenOutlined";
import FullscreenExitOutlined from "@ant-design/icons/FullscreenExitOutlined";
import ExportOutlined from "@ant-design/icons/ExportOutlined";
import { Alert, Button, Card, Collapse, Empty, Select, Skeleton } from "antd";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import { createPortal } from "react-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchPublicCemeteryMap, PublicCemeteryMapError, type PublicCemeteryMapData, type PublicCemeteryMapPoint } from "./publicCemeteryMapApi";
import "./publicCemeteryMap.css";

type Basemap = "street" | "satellite" | "hybrid";
const basemaps = [ { value: "street", label: "Đường phố" }, { value: "satellite", label: "Vệ tinh" }, { value: "hybrid", label: "Vệ tinh có nhãn" } ];
const districtOptions = [
  { value: "ALL", label: "Tất cả địa bàn (13 huyện/thị)" },
  { value: "TP Quảng Ngãi", label: "TP. Quảng Ngãi" },
  { value: "Ba Tơ", label: "Huyện Ba Tơ" },
  { value: "Mộ Đức", label: "Huyện Mộ Đức" },
  { value: "Bình Sơn", label: "Huyện Bình Sơn" },
  { value: "Tư Nghĩa", label: "Huyện Tư Nghĩa" },
  { value: "Đức Phổ", label: "Thị xã Đức Phổ" },
  { value: "Nghĩa Hành", label: "Huyện Nghĩa Hành" },
  { value: "Trà Bồng", label: "Huyện Trà Bồng" },
  { value: "Sơn Tịnh", label: "Huyện Sơn Tịnh" },
  { value: "Sơn Hà", label: "Huyện Sơn Hà" },
  { value: "Sơn Tây", label: "Huyện Sơn Tây" },
  { value: "Minh Long", label: "Huyện Minh Long" },
  { value: "Lý Sơn", label: "Huyện Lý Sơn" },
];
// Hình tượng đài liệt sĩ theo bản vẽ gốc: từng khối nền trắng nét đen, vẽ chồng từ sau ra trước; sao và khung bia màu đỏ.
const monumentSvg = `<svg viewBox="0 0 496 1100" aria-hidden="true"><g fill="#fff" stroke="#1f1f1f">`
  + `<path d="M248 35 188 35 183 15 148 43 125 480 175 480 175 1055 321 1055 321 480 371 480 348 43 313 15 308 35Z"/>`
  + `<path d="M117 485 152 475 82 760 147 833 147 1055 107 1055 107 843 30 765Z M379 485 344 475 414 760 349 833 349 1055 389 1055 389 843 466 765Z"/>`
  + `<path d="M10 885 60 885 90 915 147 940 147 1055 88 1055 87 1040 82 1025 75 1012 65 1000 53 990 40 983 25 978 10 977Z M486 885 436 885 406 915 349 940 349 1055 408 1055 409 1040 414 1025 421 1012 431 1000 443 990 456 983 471 978 486 977Z"/>`
  + `<path d="M165 1005 331 1005 331 1055 165 1055Z M25 1055 471 1055 471 1100 25 1100Z"/><circle cx="248" cy="100" r="48"/></g>`
  + `<path d="M248 66 256 89 280 89 261 104 268 128 248 114 228 128 235 104 216 89 240 89Z" fill="#e00"/><rect x="198" y="165" width="97" height="840" fill="none" stroke="#e00"/></svg>`;

// Có mã địa điểm thì Google mở đúng thẻ nghĩa trang; chưa có thì chỉ mở theo tọa độ, không bao giờ tìm theo tên (CR-QNG-006).
const googleMapsUrl = (point: PublicCemeteryMapPoint) => "https://www.google.com/maps/search/?api=1&query=" + (point.googlePlaceId
  ? `${encodeURIComponent(point.name)}&query_place_id=${point.googlePlaceId}`
  : `${point.latitude},${point.longitude}`);

export function PublicCemeteryMap() {
  const [snapshot, setSnapshot] = useState<PublicCemeteryMapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ requestId?: string } | null>(null);
  const pending = useRef<AbortController | null>(null);
  const [basemap, setBasemap] = useState<Basemap>("street");
  const [districtFilter, setDistrictFilter] = useState<string>("ALL");
  const [tileError, setTileError] = useState(false);
  const [selection, setSelection] = useState<{ point: PublicCemeteryMapPoint; host: HTMLDivElement } | null>(null);
  const container = useRef<HTMLDivElement | null>(null);
  const fullscreenHost = useRef<HTMLDivElement | null>(null);
  const fullscreenButton = useRef<HTMLButtonElement | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [fullscreenError, setFullscreenError] = useState(false);
  const map = useRef<L.Map | null>(null);
  const layers = useRef<Record<Basemap, L.TileLayer> | null>(null);
  const markers = useRef<L.Marker[]>([]);

  const load = useCallback(async () => {
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPublicCemeteryMap(controller.signal);
      if (!controller.signal.aborted) setSnapshot(data);
    } catch (cause) {
      if (!controller.signal.aborted) setError(cause instanceof PublicCemeteryMapError && cause.requestId ? { requestId: cause.requestId } : {});
    } finally { if (!controller.signal.aborted) setLoading(false); }
  }, []);

  useEffect(() => {
    void load();
    const refresh = () => { void load(); };
    window.addEventListener("focus", refresh);
    return () => { pending.current?.abort(); window.removeEventListener("focus", refresh); };
  }, [load]);

  useEffect(() => {
    const onFullscreenChange = () => {
      const active = document.fullscreenElement === fullscreenHost.current;
      setFullscreen(active);
      if (!active) fullscreenButton.current?.focus();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && document.fullscreenElement === fullscreenHost.current) {
        void document.exitFullscreen().catch(() => setFullscreenError(true));
      }
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const toggleFullscreen = async () => {
    setFullscreenError(false);
    try {
      if (document.fullscreenElement === fullscreenHost.current) await document.exitFullscreen();
      else await fullscreenHost.current?.requestFullscreen();
    } catch { setFullscreenError(true); }
  };

  const points = snapshot?.points;
  const mappedPoints = useMemo(() => points?.filter((point): point is PublicCemeteryMapPoint & { latitude: number; longitude: number } =>
    point.latitude !== null && point.longitude !== null), [points]);
  useEffect(() => {
    const element = container.current;
    if (!element || !mappedPoints?.length) return;
    const instance = L.map(element, { scrollWheelZoom: true, minZoom: 3, maxZoom: 20 });
    map.current = instance;
    const googleTile = (lyrs: string) => L.tileLayer(`https://mt{s}.google.com/vt/lyrs=${lyrs}&x={x}&y={y}&z={z}`, {
      subdomains: "0123", maxZoom: 21, attribution: "&copy; Google",
    }).on("tileerror", () => setTileError(true));
    layers.current = { street: googleTile("m"), satellite: googleTile("s"), hybrid: googleTile("y") };
    layers.current.street.addTo(instance);
    L.control.scale({ imperial: false, position: "bottomleft" }).addTo(instance);
    instance.on("popupclose", () => setSelection(null));
    const bounds = L.latLngBounds(mappedPoints.map(point => [point.latitude, point.longitude]));
    instance.setView(bounds.getCenter(), 8);
    if (instance.getSize().x > 0 && instance.getSize().y > 0) instance.fitBounds(bounds, { padding: [36, 36], maxZoom: 13 });
    markers.current = mappedPoints.map((point) => {
      const marker = L.marker([point.latitude, point.longitude], {
        title: point.name, keyboard: true,
        zIndexOffset: point.scenePath ? 1000 : 0,
        icon: L.divIcon({ className: "pub-map-marker", html: `<span class="pub-map-pin${point.scenePath ? " pub-map-pin-ready" : ""}">${monumentSvg}</span>`, iconSize: [22, 48], iconAnchor: [11, 48] }),
      }).addTo(instance);
      const markerElement = marker.getElement();
      markerElement?.setAttribute("aria-label", `${point.name}, ${point.graveCount} mộ, ${point.scenePath ? "có 3D" : "chưa có 3D"}`);
      const host = document.createElement("div");
      marker.bindPopup(host, { minWidth: 250, maxWidth: 340 });
      marker.on("popupopen", () => setSelection({ point, host }));
      markerElement?.addEventListener("keydown", event => {
        if (event.key === " ") { event.preventDefault(); marker.openPopup(); }
        if (event.key === "Escape") { instance.closePopup(); markerElement.focus(); }
      });
      return marker;
    });
    const observer = new ResizeObserver(() => instance.invalidateSize({ pan: false }));
    observer.observe(element);
    return () => {
      observer.disconnect(); instance.off(); instance.remove();
      map.current = null; layers.current = null; markers.current = [];
    };
  }, [mappedPoints]);

  useEffect(() => {
    const instance = map.current;
    const currentLayers = layers.current;
    if (!instance || !currentLayers) return;
    for (const [key, layer] of Object.entries(currentLayers)) {
      if (key === basemap) layer.addTo(instance);
      else if (instance.hasLayer(layer)) instance.removeLayer(layer);
    }
  }, [basemap, points]);

  useEffect(() => {
    if (selection) {
      selection.host.querySelector<HTMLAnchorElement>("a")?.focus();
      markers.current.find(marker => marker.getLatLng().lat === selection.point.latitude && marker.getLatLng().lng === selection.point.longitude)?.getPopup()?.update();
    }
  }, [selection]);

  return (
    <Card className="pub-cemetery-map" id="tham-quan" role="region" aria-labelledby="pub-map-title">
      <div className="pub-map-heading"><h2 id="pub-map-title">Bản đồ nghĩa trang liệt sĩ</h2><p>Cuộn chuột để phóng/thu, kéo để di chuyển. Chọn ghim để xem thông tin và tham quan 3D nếu có.</p></div>
      {error ? <Alert role="alert" type={snapshot ? "warning" : "error"} title={snapshot ? "Bản đồ cũ: chưa cập nhật được dữ liệu mới." : "Không tải được dữ liệu nghĩa trang."} description={error.requestId ? `Mã hỗ trợ: ${error.requestId}` : undefined} action={<Button onClick={() => { void load(); }}>Thử lại</Button>} /> : null}
      {loading && !snapshot ? <Skeleton active aria-label="Đang tải bản đồ nghĩa trang" /> : null}
      {snapshot && points?.length === 0 ? <Empty description="Chưa có nghĩa trang được công bố trên bản đồ." /> : null}
      {points?.length ? <>
        {!mappedPoints?.length ? <Empty description="Chưa có tọa độ. Vẫn có thể xem danh sách nghĩa trang bên dưới." /> : null}
        <div className="pub-map-toolbar">
          <label htmlFor="pub-map-basemap">Nền bản đồ</label>
          <Select id="pub-map-basemap" aria-label="Nền bản đồ" value={basemap} options={basemaps} onChange={setBasemap} />
          <label htmlFor="pub-map-district" style={{ marginLeft: "0.5rem" }}>Địa bàn</label>
          <Select
            id="pub-map-district"
            aria-label="Địa bàn"
            value={districtFilter}
            options={districtOptions}
            onChange={(val) => {
              setDistrictFilter(val);
              map.current?.closePopup();
              if (val === "ALL") {
                if (mappedPoints?.length) {
                  map.current?.fitBounds(L.latLngBounds(mappedPoints.map(p => [p.latitude, p.longitude])), { padding: [36, 36], maxZoom: 13 });
                }
              } else {
                const matched = mappedPoints?.filter(p => p.name.includes(val) || p.address.includes(val));
                if (matched && matched.length > 0) {
                  map.current?.fitBounds(L.latLngBounds(matched.map(p => [p.latitude, p.longitude])), { padding: [36, 36], maxZoom: 14 });
                }
              }
            }}
          />
          <Button onClick={() => {
            setDistrictFilter("ALL");
            map.current?.closePopup();
            if (mappedPoints?.length) map.current?.fitBounds(L.latLngBounds(mappedPoints.map(point => [point.latitude, point.longitude])), { padding: [36, 36], maxZoom: 13 });
          }}>Về tổng quan</Button>
        </div>
        <div ref={fullscreenHost} className="pub-map-viewport">
          <div ref={container} className="pub-map-canvas" role="group" aria-label="Bản đồ điểm nghĩa trang Quảng Ngãi" aria-busy={loading} />
          <Button ref={fullscreenButton} className="pub-map-fullscreen" aria-label={fullscreen ? "Thu nhỏ" : "Toàn màn hình"} icon={fullscreen ? <FullscreenExitOutlined aria-hidden="true" /> : <FullscreenOutlined aria-hidden="true" />} disabled={!document.fullscreenEnabled} title={!document.fullscreenEnabled ? "Trình duyệt không hỗ trợ toàn màn hình" : fullscreen ? "Thu nhỏ" : "Toàn màn hình"} onClick={() => { void toggleFullscreen(); }} />
        </div>
        {fullscreenError ? <Alert role="alert" type="warning" title="Không đổi được chế độ toàn màn hình. Bạn vẫn có thể sử dụng bản đồ." /> : null}
        <div className="pub-map-legend"><span><i className="pub-map-dot pub-map-dot-ready" aria-hidden="true" />Có tham quan 3D</span><span><i className="pub-map-dot" aria-hidden="true" />Chưa có dữ liệu 3D</span></div>
        {tileError ? <Alert role="status" type="warning" title="Không tải được một phần bản đồ nền. Vẫn có thể chọn ghim hoặc mở danh sách nghĩa trang." /> : null}
        <Collapse ghost items={[{ key: "cemeteries", label: "Chọn từ danh sách nghĩa trang", children: <ul className="pub-map-list">{points.map((point, index) => <li key={index}><Button disabled={point.latitude === null || point.longitude === null} onClick={() => {
          if (point.latitude === null || point.longitude === null) return;
          map.current?.setView([point.latitude, point.longitude], 13);
          const markerIndex = mappedPoints?.findIndex(mapped => mapped === point) ?? -1;
          markers.current[markerIndex]?.openPopup();
        }}><strong>{point.name}</strong><span>{point.graveCount.toLocaleString("vi-VN")} mộ · {point.scenePath ? "Có 3D" : "Chưa có 3D"}{point.latitude === null || point.longitude === null ? " · Chưa có tọa độ" : ""}</span></Button></li>)}</ul> }]} />
      </> : null}
      {selection ? createPortal(<div className="pub-map-popup" role="dialog" aria-label={selection.point.name} onKeyDown={event => {
        if (event.key === "Escape") { map.current?.closePopup(); markers.current.find(marker => marker.getLatLng().lat === selection.point.latitude && marker.getLatLng().lng === selection.point.longitude)?.getElement()?.focus(); }
      }}><h3>{selection.point.name}</h3><p>{selection.point.address}</p><div className="pub-map-popup-count"><strong>{selection.point.graveCount.toLocaleString("vi-VN")}</strong> mộ</div><div className="pub-map-popup-actions">{selection.point.scenePath ? <Button className="pub-map-scene-button" href={selection.point.scenePath}>Xem nghĩa trang 3D</Button> : <p className="pub-map-unavailable">Chưa có dữ liệu 3D</p>}<Button className="pub-map-search-btn" href={`/danh-sach-liet-si?cemetery=${encodeURIComponent(selection.point.name)}`}>Tra cứu mộ tại đây</Button></div><Button type="link" className="pub-map-google-link" icon={<ExportOutlined aria-hidden="true" />} iconPlacement="end" href={googleMapsUrl(selection.point)} target="_blank" rel="noopener noreferrer">Mở trên Google Maps</Button></div>, selection.host) : null}
    </Card>
  );
}
